import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { HslBand } from '@lumos/core';
import {
  HSL_BANDS,
  HSL_BAND_HUE,
  applyMatrix,
  defaultRecipe,
  movedHslBands,
  whiteBalanceMatrix,
} from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { GroupLabel, Panel, Segmented } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';
import { Tip } from '../controls/Tooltip.tsx';
import { GradingWheels } from '../ColorWheel.tsx';

const DEFAULTS = defaultRecipe();

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  margin: '4px 0 2px',
};

/** The band picker wraps instead of overflowing when the rail is dragged narrow. */
const BAND_PICKER: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
  margin: '2px 0 4px',
};

const COUNT: CSSProperties = { marginLeft: 8, opacity: 0.75 };

const BAND_LABEL: Record<HslBand, string> = {
  red: 'Red',
  orange: 'Orange',
  yellow: 'Yellow',
  green: 'Green',
  aqua: 'Aqua',
  blue: 'Blue',
  purple: 'Purple',
  magenta: 'Magenta',
};

/**
 * The bands in pairs. One eight-way switch cannot fit the narrowest rail, and
 * a segmented control cannot wrap inside itself, so the pairs wrap instead.
 */
const BAND_PAIRS: HslBand[][] = HSL_BANDS.reduce<HslBand[][]>((groups, entry, index) => {
  if (index % 2 === 0) groups.push([entry]);
  else groups[groups.length - 1].push(entry);
  return groups;
}, []);

/** Mid-grey in linear light: bright enough to show the cast, far from clipping. */
const GREY = 0.55;

function encodeChannel(linear: number): number {
  const c = Math.min(1, Math.max(0, linear));
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}

function hexOf(rgb: [number, number, number]): string {
  return `#${rgb
    .map((channel) => Math.round(encodeChannel(channel) * 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Paints the track with what the control actually does: each stop is a neutral
 * grey pushed through the same white-balance matrix the renderer uses, for that
 * position on the slider. So the gradient is the real response for *this*
 * file's as-shot reference rather than a decorative blue-to-orange ramp, and
 * the neutral point sits wherever the camera actually put it.
 */
function balanceGradient(
  samples: readonly { kelvin: number; tint: number }[],
  asShotKelvin: number,
  asShotTint: number,
): string {
  const stops = samples.map((sample, index) => {
    const matrix = whiteBalanceMatrix(sample.kelvin, sample.tint, asShotKelvin, asShotTint);
    const rgb = applyMatrix(matrix, [GREY, GREY, GREY]);
    const position = (index / (samples.length - 1)) * 100;
    return `${hexOf(rgb)} ${position.toFixed(1)}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

export function ColorPanel() {
  const photo = useSelectedPhoto();
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);
  const [band, setBand] = useState<HslBand>('red');

  const asShot = photo?.image?.asShot ?? null;
  const asShotKelvin = asShot?.kelvin ?? 5500;
  const asShotTint = asShot?.tint ?? 0;

  const temperatureTrack = useMemo(() => {
    const samples = Array.from({ length: 13 }, (_, index) => ({
      kelvin: 2000 + (18000 * index) / 12,
      tint: asShotTint,
    }));
    return balanceGradient(samples, asShotKelvin, asShotTint);
  }, [asShotKelvin, asShotTint]);

  const tintTrack = useMemo(() => {
    const samples = Array.from({ length: 13 }, (_, index) => ({
      kelvin: asShotKelvin,
      tint: -150 + (300 * index) / 12,
    }));
    return balanceGradient(samples, asShotKelvin, asShotTint);
  }, [asShotKelvin, asShotTint]);

  if (!photo) return null;
  const recipe = photo.recipe;

  const bandsMoved = movedHslBands(recipe);

  const gradingTouched =
    (['shadows', 'midtones', 'highlights', 'global'] as const).some((zone) => {
      const wheel = recipe.grading[zone];
      return wheel.hue !== 0 || wheel.saturation !== 0 || wheel.luminance !== 0;
    }) ||
    recipe.grading.blending !== DEFAULTS.grading.blending ||
    recipe.grading.balance !== DEFAULTS.grading.balance;

  const active =
    recipe.temperature !== null ||
    recipe.tint !== null ||
    recipe.vibrance !== DEFAULTS.vibrance ||
    recipe.saturation !== DEFAULTS.saturation ||
    bandsMoved > 0 ||
    gradingTouched;

  const hue = HSL_BAND_HUE[band];
  const adjustment = recipe.hsl[band];

  return (
    <Panel id="color" title="Colour" active={active}>
      <GroupLabel>White balance</GroupLabel>
      <div style={ROW}>
        <Tip title="As shot" body="Puts white balance back to what the camera recorded.">
          <button
            type="button"
            className="btn tiny"
            disabled={recipe.temperature === null && recipe.tint === null}
            onClick={() =>
              updateRecipe(
                (draft) => {
                  draft.temperature = null;
                  draft.tint = null;
                },
                { commit: true },
              )
            }
          >
            As shot
          </button>
        </Tip>
        <span className="label">
          {asShot
            ? `${asShot.estimated ? 'Camera' : 'Assumed'} ${Math.round(asShotKelvin)}K · tint ${
                asShotTint > 0 ? '+' : ''
              }${Math.round(asShotTint)}`
            : 'Available once the file is decoded'}
        </span>
      </div>

      <Slider
        label="Temperature"
        value={recipe.temperature ?? asShotKelvin}
        min={2000}
        max={20000}
        step={10}
        neutral={asShotKelvin}
        unit="K"
        gradient={temperatureTrack}
        disabled={!asShot}
        help="Blue to yellow balance. Judge it on something you know is neutral, never on skin."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.temperature = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Tint"
        value={recipe.tint ?? asShotTint}
        min={-150}
        max={150}
        step={1}
        neutral={asShotTint}
        gradient={tintTrack}
        disabled={!asShot}
        help="Green to magenta balance. Fluorescent light usually wants magenta, shade under trees wants less green."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.tint = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>Saturation</GroupLabel>
      <Slider
        label="Vibrance"
        value={recipe.vibrance}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Lifts the muted colours first and holds skin back. Usually the one to reach for on portraits."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.vibrance = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Saturation"
        value={recipe.saturation}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Lifts every colour equally. Good for taking colour out, blunt for putting it in."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.saturation = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>
        <Tip
          title="Colour mixer"
          body="Changes one range of colour at a time and leaves the rest of the frame alone."
        >
          Colour mixer
        </Tip>
        {bandsMoved > 0 ? (
          <span style={COUNT}>
            {bandsMoved} band{bandsMoved === 1 ? '' : 's'} set
          </span>
        ) : null}
      </GroupLabel>
      <div style={BAND_PICKER}>
        {BAND_PAIRS.map((pair) => (
          <Segmented
            key={pair[0]}
            value={band}
            options={pair.map((entry) => ({ value: entry, label: BAND_LABEL[entry] }))}
            onChange={setBand}
            ariaLabel={`Colour mixer band, ${pair.map((entry) => BAND_LABEL[entry]).join(' or ')}`}
          />
        ))}
      </div>

      <Slider
        label={`${BAND_LABEL[band]} hue`}
        value={adjustment.hue}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        gradient={`linear-gradient(90deg, hsl(${hue - 30} 72% 52%), hsl(${hue} 72% 52%), hsl(${
          hue + 30
        } 72% 52%))`}
        help={`Rotates hues inside the ${band} range. Past about 25 the band stops reading as ${band}.`}
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.hsl[band].hue = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label={`${BAND_LABEL[band]} saturation`}
        value={adjustment.saturation}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        gradient={`linear-gradient(90deg, hsl(${hue} 3% 52%), hsl(${hue} 42% 52%), hsl(${hue} 92% 52%))`}
        help={`Strengthens or mutes the ${band} range only. Faster than masking a colour that distracts.`}
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.hsl[band].saturation = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label={`${BAND_LABEL[band]} luminance`}
        value={adjustment.luminance}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        gradient={`linear-gradient(90deg, hsl(${hue} 62% 12%), hsl(${hue} 62% 52%), hsl(${hue} 62% 90%))`}
        help={`Brightens or darkens the ${band} range only. Dropping blue is how you deepen a sky.`}
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.hsl[band].luminance = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>
        <Tip
          title="Colour grading"
          body="Tints the shadows, mid-tones and highlights separately. Small saturation goes a long way."
        >
          Colour grading
        </Tip>
      </GroupLabel>
      <GradingWheels />
      <Slider
        label="Blending"
        value={recipe.grading.blending}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.grading.blending}
        help="How far the three grades overlap. Low keeps them separate, high melts them into one wash."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.grading.blending = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Balance"
        value={recipe.grading.balance}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Moves the split point between the shadow and highlight grades."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.grading.balance = value;
          })
        }
        onCommit={commit}
      />
    </Panel>
  );
}
